import { useEffect, useRef } from 'react';

// Re-runs `onReconnect` whenever the browser regains network connectivity
// (the `online` event) or the tab is foregrounded again (visibilitychange ->
// visible). Contexts that fetch once on mount (Auth, Booking, Campaign) never
// retry on their own if that fetch fails while offline or while the tab was
// backgrounded/throttled — this is what makes them recover instead of being
// stuck showing stale/empty data ("no campaigns loaded") until a manual
// page refresh.
export function useRefetchOnReconnect(onReconnect: () => void): void {
  // Stored in a ref so the effect below never needs to re-subscribe just
  // because the caller passed a new function identity on re-render.
  const callbackRef = useRef(onReconnect);
  callbackRef.current = onReconnect;

  useEffect(() => {
    const handleOnline = () => callbackRef.current();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        callbackRef.current();
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);
}
